import { useState } from "react";
import { Card } from "@/ui/card";
import { Spinner } from "@/ui/spinner";
import { useAcoes, useEstado } from "../estado.jsx";
import { forcaDaSenha } from "../senha.js";
import { Botao, Campo } from "./comuns.jsx";

/* Quatro segmentos, no idioma do trilho do processo: a cor faz o trabalho e a
   palavra ao lado diz o mesmo, porque estado não depende só de cor. */
const COR_DO_NIVEL = ["bg-terra", "bg-terra", "bg-ambar", "bg-verde", "bg-verde"];

/**
 * A troca da senha provisória — a primeira tela de quem acabou de ganhar acesso.
 *
 * A troca era "obrigatória" só no nome: o login nascia com
 * `precisa_trocar_senha` e nenhuma tela olhava a coluna. Quem criou o acesso
 * conhece a provisória, então ela valia como senha de verdade para sempre. Agora
 * a portaria recusa o escritório até a troca, e esta tela é a única coisa que a
 * mesa mostra enquanto isso — no computador e no celular, que é onde a pessoa
 * costuma abrir o link que recebeu.
 *
 * A régua da força é a do servidor (`senha.js`), respondendo enquanto se digita:
 * o botão só acende quando a senha vai ser aceita.
 */
export default function TrocaDeSenha() {
  const { trocaDeSenha: pessoa } = useEstado();
  const { trocarSenhaProvisoria, sair } = useAcoes();
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [repetida, setRepetida] = useState("");
  const [erro, setErro] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const forca = forcaDaSenha(nova, { nome: pessoa?.nome, email: pessoa?.email });
  const diferentes = repetida.length > 0 && repetida !== nova;
  const pronta = atual && !forca.problema && repetida === nova;
  const primeiroNome = pessoa?.nome?.trim().split(/\s+/)[0] ?? "";

  async function enviar() {
    if (!pronta || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await trocarSenhaProvisoria(atual, nova);
    } catch (e) {
      /* "Senha atual incorreta" é o caso comum (a provisória foi colada com um
         espaço) e é dito ao lado do campo, onde a pessoa vai corrigir. */
      setErro(e.message);
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <Card className="w-[min(420px,100%)] gap-0 rounded-[20px] border border-border p-[26px] shadow-[0_18px_44px_color-mix(in_oklab,_var(--color-foreground)_10%,_transparent)] ring-0 [--card-spacing:0px] max-[759px]:p-5">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
        >
          <img src="/fga-mark.png" alt="Galera Advocacia" className="mb-4 block h-[38px] w-auto" />
          <h1 className="m-0 mb-1.5 font-serif text-[27px] leading-tight font-normal">
            {primeiroNome ? `Bem-vinda(o), ${primeiroNome}.` : "Bem-vinda(o)."}
          </h1>
          <p className="m-0 mb-5 text-corpo leading-[1.55] text-tinta-suave">
            Você entrou com uma senha provisória — quem criou seu acesso também a conhece. Escolha uma sua para abrir a mesa.
          </p>

          <Campo
            rotulo="Senha provisória"
            mostrarRotulo
            tipo="password"
            autoPreencher="current-password"
            dica="a que você recebeu"
            valor={atual}
            aoMudar={(v) => {
              setAtual(v);
              setErro(null);
            }}
            foco
            moldura="margin-bottom:12px"
          />
          <Campo
            rotulo="Senha nova"
            mostrarRotulo
            ajuda="Doze caracteres ou mais. Uma frase curta que só você entende é mais forte que uma palavra com símbolos."
            tipo="password"
            autoPreencher="new-password"
            dica="senha nova"
            valor={nova}
            aoMudar={setNova}
          />
          <div className="mt-2 mb-3" aria-live="polite">
            <div className="flex gap-1" aria-hidden="true">
              {[1, 2, 3, 4].map((i) => (
                <span
                  key={i}
                  className={`h-[5px] flex-1 rounded-full transition-colors duration-150 ${
                    nova && forca.nivel >= i ? COR_DO_NIVEL[forca.nivel] : "bg-foreground/10"
                  }`}
                />
              ))}
            </div>
            {nova ? (
              <div className={`mt-1.5 text-apoio leading-[1.4] ${forca.problema ? "text-terra-escura" : "text-verde"}`}>
                <b className="font-semibold">{forca.rotulo}.</b> {forca.problema ?? "O servidor vai aceitar esta senha."}
              </div>
            ) : null}
          </div>
          <Campo
            rotulo="Repita a senha nova"
            mostrarRotulo
            tipo="password"
            autoPreencher="new-password"
            dica="a mesma, de novo"
            valor={repetida}
            aoMudar={setRepetida}
          />
          {diferentes ? <div className="mt-1.5 text-apoio text-terra-escura">As duas não batem.</div> : null}

          {erro ? (
            <div role="alert" className="mt-3 text-corpo leading-[1.45] text-primary">
              {erro}
            </div>
          ) : null}

          <Botao
            rotulo={
              enviando ? (
                <>
                  <Spinner className="size-3.5" />
                  Trocando…
                </>
              ) : (
                "Trocar a senha e entrar"
              )
            }
            tipo="submit"
            destaque
            desabilitado={!pronta || enviando}
            estilo="width:100%;margin-top:18px;font-size:14px;padding:13px;border-radius:12px"
          />
          <p className="m-0 mt-3 text-center text-apoio leading-[1.45] text-muted-foreground">
            As outras sessões com esta conta são encerradas na troca.{" "}
            <button
              type="button"
              onClick={sair}
              className="cursor-pointer border-none bg-transparent p-0 text-apoio text-inherit underline decoration-dotted underline-offset-2"
            >
              Sair
            </button>
          </p>
        </form>
      </Card>
    </div>
  );
}
